import { Action, Subject } from '@erp/authz';

/**
 * Tool category classification for Agent Builder
 */
export type ToolCategory = 'Data Access' | 'Communication' | 'Document';

/**
 * Agent permission with action and subject
 */
export type AgentPermission = {
  action: Action;
  subject: Subject;
};

/**
 * Tool definition for Agent Builder
 */
export interface AgentTool {
  id: string;
  name: string;
  description: string;
  category: ToolCategory;
  requiredPermissions: AgentPermission[];
}

/**
 * Agent Builder state
 */
export interface AgentBuilderState {
  name: string;
  description: string;
  prompt: string;
  enabledTools: Set<string>;
  permissions: AgentPermission[];
}

/**
 * Agent Builder component props
 */
export interface AgentBuilderProps {
  value: AgentBuilderState;
  onChange: (state: AgentBuilderState) => void;
}

/**
 * Allowed actions for agent permissions
 */
export const ALLOWED_ACTIONS: Action[] = [
  Action.Create,
  Action.Read,
  Action.Update,
  Action.Delete,
];

/**
 * Payment-related subjects that are blocked from agent permissions
 */
export const PAYMENT_SUBJECTS: Subject[] = [
  Subject.Payment,
  Subject.APInvoicePayment,
  Subject.ARPayment,
];

/**
 * Generate a unique key for a permission
 */
export function permissionKey(action: Action, subject: Subject): string {
  return `${action}:${subject}`;
}

export function isAllowableAgentPermission(
  action: Action,
  subject: Subject,
): boolean {
  if (!ALLOWED_ACTIONS.includes(action)) {
    return false;
  }

  if (action === Action.Read && subject === Subject.APInvoicePayment) {
    return true;
  }

  return !PAYMENT_SUBJECTS.includes(subject);
}

/**
 * Check if a permission is blocked
 */
export function isBlockedPermission(action: Action, subject: Subject): boolean {
  return !isAllowableAgentPermission(action, subject);
}

/**
 * Human-readable labels for actions
 */
export const ACTION_LABELS: Record<Action, string> = {
  [Action.Create]: 'Create',
  [Action.Read]: 'View',
  [Action.Update]: 'Edit',
  [Action.Delete]: 'Delete',
  [Action.Approve]: 'Approve',
  [Action.Execute]: 'Execute',
  [Action.Manage]: 'Manage',
  [Action.Report]: 'Report',
};

export function formatSubject(subject: Subject): string {
  return subject
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
}

/**
 * Format a permission to human-readable text
 * e.g., {action: Action.Create, subject: Subject.Invoice} -> "Create Invoice"
 */
export function formatPermission(perm: AgentPermission): string {
  return `${ACTION_LABELS[perm.action]} ${formatSubject(perm.subject)}`;
}
